import { FC, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AiChatMessage } from "../interfaces";
import { BarChart, Bar, BarShapeProps, Rectangle, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer } from "recharts";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { downloadChartAsPng, getChartColors, parseChartEntries } from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatChartHelper";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { formatDate, formatDateCustom, formatDateTime } from "@app/shared/formatDateTime";
import { Modal } from "@app/shared/Modals";
import { CancelButtonType } from "@app/shared/Modals/modalContent";
import { QESButton } from "@app/shared/qes-standards/Button";
import { useMeasuredSize } from "@app/projects/network-assessment/provider-hub/ai-assistant/hooks/useMeasuredSize";
import { MeasureTextWidthPx, useTextMeasurer } from "@app/projects/network-assessment/provider-hub/ai-assistant/hooks/useTextMeasurer";

interface ChartDataEntry {
  label: string;
  value: number;
}

interface AIChatBarChartProps {
  message: AiChatMessage;
}

const CHART_COLORS = getChartColors();

const MIN_BOTTOM_MARGIN = 5;
// recharts adds the axis sizes on top of the margins: offset.left = margin.left + yAxis.width and
// offset.bottom = margin.bottom + xAxis.height. These are its defaults, and are already reserved.
const Y_AXIS_WIDTH_PX = 60;
const X_AXIS_HEIGHT_PX = 30;
// Within that x-axis band, recharts drops the tick text by tickSize + tickMargin before drawing it,
// so only the remainder is usable space for the label itself.
const TICK_LINE_AND_MARGIN_PX = 8;
const X_AXIS_LABEL_SPACE_PX = X_AXIS_HEIGHT_PX - TICK_LINE_AND_MARGIN_PX;
const MIN_LEFT_MARGIN = 0;
const RIGHT_MARGIN_PX = 5;
const TOP_MARGIN_PX = 20;

const DEFAULT_TICK_FONT_SIZE_PX = 12;
const MIN_TICK_FONT_SIZE_PX = 9;

const FALLBACK_CHART_HEIGHT_PX = 300;

/** Below this the bars stop conveying anything, however well the labels fit. */
const MIN_PLOT_HEIGHT_PX = 140;
/** Rotated labels may never claim more than this share of the chart box. */
const MAX_AXIS_BAND_FRACTION = 0.45;
/** Vertical space one horizontal bar gets, including the gap to its neighbour. */
const BAR_SLOT_PX = 28;
/** Ceiling on the horizontal layout's label gutter, as a share of the chart width. */
const MAX_Y_AXIS_FRACTION = 0.45;
/** Clearance past the longest bar's end so its value label stays inside the SVG. */
const VALUE_LABEL_PADDING_PX = 10;

export type ChartOrientation = "horizontal-bars" | "vertical-bars";

/** Geometry for the modal chart, all of which depends on the measured box. */
export interface ModalChartLayout {
  /** Always a number, so ResponsiveContainer never has to resolve a percentage height. */
  chartHeightPx: number;
  fontSizePx: number;
  orientation: ChartOrientation;
  /** vertical-bars only. */
  bottomMarginPx: number;
  /** vertical-bars only. */
  leftMarginPx: number;
  /** horizontal-bars only. */
  rightMarginPx: number;
  /** horizontal-bars only. */
  yAxisWidthPx: number;
}

/** Width available to the bars themselves, before any left margin is reserved. */
const barRegionWidthPx = (chartWidthPx: number): number =>
  Math.max(0, chartWidthPx - Y_AXIS_WIDTH_PX - RIGHT_MARGIN_PX);

/** Where bar `index` of `count` sits across the bar region, as a fraction. Always < 1. */
const barPositionFraction = (index: number, count: number): number => (index + 0.5) / count;

/**
 * How far left of its own tick a -45° rotated label reaches.
 *
 * Rotating the text box by -45° maps local (x, y) to (0.707(x + y), 0.707(y - x)). With the text
 * anchored at its end, x runs over [-width, 0] and y over [0, lineHeight], so the leftmost point
 * is 0.707 x width — the line height pushes the glyphs right and down, never left.
 */
const labelLeftExtentPx = (label: string, fontSizePx: number, measureTextWidthPx: MeasureTextWidthPx): number =>
  measureTextWidthPx(label, fontSizePx) * Math.SQRT1_2;

/**
 * How far below its own tick a -45° rotated label reaches.
 *
 * Same mapping as above, but downward the line height adds instead of cancelling: the lowest point
 * is 0.707 × (width + lineHeight). Leaving the line height out here is what clipped the last
 * characters of long labels.
 */
const labelBottomExtentPx = (label: string, fontSizePx: number, measureTextWidthPx: MeasureTextWidthPx): number =>
  (measureTextWidthPx(label, fontSizePx) + fontSizePx) * Math.SQRT1_2;

/**
 * Largest legible font for `count` bars sharing `availableWidthPx`.
 *
 * Adjacent -45° labels are separated by `bandWidth × sin 45°` measured perpendicular to the text,
 * so they stay readable while that gap is at least the font size.
 */
const fontSizeForWidth = (availableWidthPx: number, count: number): number => {
  const perpendicularGapPx = (availableWidthPx / count) * Math.SQRT1_2;
  return Math.min(DEFAULT_TICK_FONT_SIZE_PX, Math.max(MIN_TICK_FONT_SIZE_PX, Math.floor(perpendicularGapPx)));
};

/**
 * Left margin needed at a given font size so no rotated label is clipped by the SVG.
 *
 * Every bar gets a vote, discounted by how far right it sits: a very long second or third label
 * still widens the margin, just less than the same label would in the leftmost slot, and a label
 * past the midpoint claims nothing because it cannot overhang the left edge at all.
 *
 * Reserving margin also narrows the bar region, pulling every bar back toward the left edge, so
 * the requirement is self-referential. Solving `left + yAxis + fraction × (barRegion - left) >=
 * extent` for `left` closes that loop exactly.
 */
const leftMarginForFontSize = (
  data: ChartDataEntry[],
  chartWidthPx: number,
  fontSizePx: number,
  measureTextWidthPx: MeasureTextWidthPx
): number => {
  const barRegionPx = barRegionWidthPx(chartWidthPx);
  const required = data.map((entry, index) => {
    const fraction = barPositionFraction(index, data.length);
    const overhangPx =
      labelLeftExtentPx(entry.label, fontSizePx, measureTextWidthPx) - Y_AXIS_WIDTH_PX - (fraction * barRegionPx);
    return overhangPx <= 0 ? 0 : overhangPx / (1 - fraction);
  });
  return Math.ceil(Math.max(MIN_LEFT_MARGIN, ...required));
};

/**
 * Resolves font size and margins together, because they depend on each other: reserving left
 * margin narrows the bar region, which can crowd the labels enough to want a smaller font, which
 * in turn needs less margin.
 *
 * Left margin is monotone non-increasing in font size, so scanning the four possible sizes from
 * largest down and taking the first that still fits once its own margin is subtracted lands on
 * the largest self-consistent pair. The smallest size is the floor and is accepted unconditionally.
 */
const computeRotatedLayout = (
  data: ChartDataEntry[],
  chartWidthPx: number,
  measureTextWidthPx: MeasureTextWidthPx
): { bottomMarginPx: number; fontSizePx: number; leftMarginPx: number } => {
  const barRegionPx = barRegionWidthPx(chartWidthPx);
  let fontSizePx = MIN_TICK_FONT_SIZE_PX;
  let leftMarginPx = leftMarginForFontSize(data, chartWidthPx, MIN_TICK_FONT_SIZE_PX, measureTextWidthPx);

  for (let candidatePx = DEFAULT_TICK_FONT_SIZE_PX; candidatePx > MIN_TICK_FONT_SIZE_PX; candidatePx--) {
    const candidateLeftPx = leftMarginForFontSize(data, chartWidthPx, candidatePx, measureTextWidthPx);
    if (fontSizeForWidth(barRegionPx - candidateLeftPx, data.length) >= candidatePx) {
      fontSizePx = candidatePx;
      leftMarginPx = candidateLeftPx;
      break;
    }
  }

  const bottomExtents = data.map(entry => Math.ceil(labelBottomExtentPx(entry.label, fontSizePx, measureTextWidthPx)));
  return {
    // recharts already leaves this much room below the axis line; only ask for the remainder.
    bottomMarginPx: Math.max(MIN_BOTTOM_MARGIN, Math.max(...bottomExtents) - X_AXIS_LABEL_SPACE_PX),
    fontSizePx,
    leftMarginPx
  };
};

/**
 * Geometry for horizontal bars: labels move to the y-axis, so nothing is rotated and the font
 * stays at full size. One bar per fixed-height slot, which lets the chart outgrow its box and
 * scroll rather than compressing the bars to invisibility.
 *
 * The y-axis gutter is capped at a share of the width, but that is not truncation — recharts
 * spreads the axis `width` onto its tick `<Text>`, which word-wraps to it with no `maxLines`,
 * so an over-long label wraps onto further lines instead of being clipped or ellipsised.
 */
const computeHorizontalLayout = (
  data: ChartDataEntry[],
  chartWidthPx: number,
  availableHeightPx: number,
  measureTextWidthPx: MeasureTextWidthPx
): ModalChartLayout => {
  const fontSizePx = DEFAULT_TICK_FONT_SIZE_PX;
  const longestLabelPx = Math.max(...data.map(entry => measureTextWidthPx(entry.label, fontSizePx)));
  const longestValuePx = Math.max(...data.map(entry => measureTextWidthPx(String(entry.value), fontSizePx)));

  return {
    bottomMarginPx: MIN_BOTTOM_MARGIN,
    chartHeightPx: Math.max(availableHeightPx, data.length * BAR_SLOT_PX + X_AXIS_HEIGHT_PX + TOP_MARGIN_PX),
    fontSizePx,
    leftMarginPx: MIN_LEFT_MARGIN,
    orientation: "horizontal-bars",
    // LabelList position="right" draws past the bar end and recharts never clips it, so the
    // margin has to hold the widest value or the longest bar's label falls outside the SVG.
    rightMarginPx: Math.ceil(longestValuePx) + VALUE_LABEL_PADDING_PX,
    yAxisWidthPx: Math.min(
      Math.ceil(longestLabelPx) + TICK_LINE_AND_MARGIN_PX,
      Math.floor(chartWidthPx * MAX_Y_AXIS_FRACTION)
    )
  };
};

/**
 * Picks the orientation, then solves that orientation's geometry.
 *
 * Rotated labels grow their bottom margin without bound, which on a short box leaves no room for
 * the bars at all — the labels render and the data does not. So the rotated layout is solved
 * first and kept only while the plot it leaves behind is still worth looking at; otherwise the
 * chart turns on its side, where label length costs width (which is capped and wraps) instead of
 * height (which was uncapped and hid the bars).
 */
export const computeModalChartLayout = (
  data: ChartDataEntry[],
  chartWidthPx: number,
  availableHeightPx: number,
  measureTextWidthPx: MeasureTextWidthPx
): ModalChartLayout => {
  const chartHeightPx = availableHeightPx > 0 ? availableHeightPx : FALLBACK_CHART_HEIGHT_PX;

  if (data.length === 0 || chartWidthPx <= 0) {
    return {
      bottomMarginPx: MIN_BOTTOM_MARGIN,
      chartHeightPx,
      fontSizePx: DEFAULT_TICK_FONT_SIZE_PX,
      leftMarginPx: MIN_LEFT_MARGIN,
      orientation: "vertical-bars",
      rightMarginPx: RIGHT_MARGIN_PX,
      yAxisWidthPx: Y_AXIS_WIDTH_PX
    };
  }

  const rotated = computeRotatedLayout(data, chartWidthPx, measureTextWidthPx);
  const plotHeightPx = availableHeightPx - TOP_MARGIN_PX - X_AXIS_HEIGHT_PX - rotated.bottomMarginPx;
  // Until the box has been measured, keep the layout that has always been rendered.
  const useHorizontal =
    availableHeightPx > 0 &&
    (plotHeightPx < MIN_PLOT_HEIGHT_PX || plotHeightPx < availableHeightPx * (1 - MAX_AXIS_BAND_FRACTION));

  if (useHorizontal) {
    return computeHorizontalLayout(data, chartWidthPx, availableHeightPx, measureTextWidthPx);
  }

  return {
    ...rotated,
    chartHeightPx,
    orientation: "vertical-bars",
    rightMarginPx: RIGHT_MARGIN_PX,
    yAxisWidthPx: Y_AXIS_WIDTH_PX
  };
};

function renderLegend(data: ChartDataEntry[]) {
  return (
    <div style={{
      display: "flex",
      flexDirection: data.length <= 5 ? "column" : "row",
      flexWrap: "wrap",
      gap: data.length <= 5 ? "4px" : "4px 16px",
      paddingLeft: "10px",
      paddingTop: "8px"
    }}>
      {data.map((entry, index) => (
        <span key={entry.label} style={{ alignItems: "center", display: "flex", gap: "4px", whiteSpace: "nowrap" }}>
        <span style={{
          backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
          display: "inline-block",
          flexShrink: 0,
          height: "10px",
          width: "10px"
        }} />
          {entry.label}: {entry.value}
        </span>
      ))}
    </div>
  )
}

const AIChatBarChart: FC<AIChatBarChartProps> = ({ message }) => {
  const featureFlagContext = useContext(FeatureFlagContext);
  const projectContext = useContext(CountyProjectContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const data = useMemo(() => {
    if (!message.content || message.content.length < 2) return [];
    return parseChartEntries(message.content);
  }, [message.content]);

  // The scroll wrapper is measured rather than the chart itself: in horizontal mode the chart's
  // height is our own output, so measuring it would feed back into the next solve. The wrapper's
  // height is fixed by the modal, and the meta block's is content-driven, so both are stable.
  const [modalScrollElement, setModalScrollElement] = useState<HTMLDivElement | null>(null);
  const [modalMetaElement, setModalMetaElement] = useState<HTMLDivElement | null>(null);
  const modalScrollSize = useMeasuredSize(modalScrollElement);
  const modalMetaSize = useMeasuredSize(modalMetaElement);

  const measureTextWidthPx = useTextMeasurer(modalScrollElement);

  const modalLayout = useMemo(
    () => computeModalChartLayout(
      data,
      modalScrollSize.widthPx,
      // Floored so a fractional remainder rounds the content inside the box rather than a
      // sliver past it. The scroller reserves its gutter regardless, so this no longer decides
      // whether a scrollbar appears — it just keeps the solve working in whole pixels.
      Math.floor(Math.max(0, modalScrollSize.heightPx - modalMetaSize.heightPx)),
      measureTextWidthPx
    ),
    [data, modalScrollSize, modalMetaSize, measureTextWidthPx]
  );

  // An orientation flip settles over more than one frame: the new orientation commits before the
  // re-solved height does, leaving a window where the old tall content is still scrollable under
  // the preserved scroll position. Once the height shrinks the browser clamps to the new maximum
  // on its own — but by then the stale offset has been painted. Hence layout, not passive: reset
  // before the paint rather than after it.
  useLayoutEffect(() => {
    if (modalScrollElement) modalScrollElement.scrollTop = 0;
  }, [modalLayout.orientation, modalScrollElement]);

  const handleDownloadChart = async () => {
    if (!chartRef.current) return;
    setIsDownloading(true);
    try {
      await downloadChartAsPng(chartRef.current, `quinn-chart-${formatDateCustom(new Date(), 'yyyyMMDD_HHmmss')}.png`);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!featureFlagContext.enabledFeatureFlags.has(Features.AI_CHARTING_ENABLED)) {
    return <p className="ai-chat-panel__message__response">Sorry, something went wrong. Please contact support.</p>;
  }

  if (!message.content || message.content.length < 2) return null;

  const barShape = (shapeProps: BarShapeProps) => (
    <Rectangle {...shapeProps} fill={CHART_COLORS[shapeProps.index % CHART_COLORS.length]} />
  );
  const valueLabelStyle = { fill: "#32323C", fontSize: modalLayout.fontSizePx };

  return (
    <>
      {!!message.chartHeader && (
        <p className="ai-chat-chart__header-title">{message.chartHeader}</p>
      )}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ bottom: 5, left: 0, right: 5, top: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={false} />
          <YAxis />
          <Tooltip />
          {data.length <= 10 && (
            <Legend content={renderLegend(data)} />
          )}
          <Bar dataKey="value" name="Results" shape={barShape} />
        </BarChart>
      </ResponsiveContainer>
      <QESButton onClick={() => setModalOpen(true)} qesTestId="ai-chat-chart__open-modal" qesType="secondary">View
        Full Chart</QESButton>
      <Modal
        className="ai-chat-chart-modal"
        closeModal={() => setModalOpen(false)}
        isVisible={modalOpen}
        modalHeight={70}
        modalWidth={70}
        content={{
          body: modalOpen ? (
            <>
              {/* Outside the scroll wrapper, and so outside the html2canvas capture root: it is
                  absolutely positioned above the container's top edge and would otherwise be
                  clipped by the scroller or hold it permanently scrolled. */}
              <div className="ai-chat-chart__header" data-html2canvas-ignore="true">
                <QESButton qesType="secondary"
                           qesTestId="ai-chat-chart__download"
                           disabled={isDownloading}
                           onClick={handleDownloadChart}>
                  {isDownloading ? 'Downloading...' : 'Download Chart'}
                </QESButton>
              </div>
              {/* The scroller must stay OUTSIDE the capture root below: html2canvas roots its
                  parse at the element it is given, so an ancestor's overflow never clips the
                  capture, but a scroller inside it would. Keeps the exported PNG complete when
                  a tall horizontal chart overflows the modal. */}
              <div className="ai-chat-chart__modal-scroll ai-chat-chart__modal-scroll--bars"
                   ref={setModalScrollElement}>
                <div className="ai-chat-chart__container" ref={chartRef}>
                  <div className="ai-chat-chart__meta" ref={setModalMetaElement}>
                    {!!message.chartHeader && (
                      <p className="ai-chat-chart__header-title ai-chat-chart__header-title-modal">{message.chartHeader}</p>
                    )}
                    <div className="ai-chat-chart__date-row" data-testid="ai-chat-chart__date-row">
                      <span><strong>Chart Created On:</strong> {formatDate(new Date())}</span>
                      <span><strong>Timeslice Date:</strong> {formatDateTime(projectContext.runConfiguration.dateCreated)}</span>
                    </div>
                  </div>
                  <div className="ai-chat-chart__modal-chart" data-testid="ai-chat-chart__modal-chart">
                    <ResponsiveContainer width="100%" height={modalLayout.chartHeightPx}>
                      {modalLayout.orientation === "horizontal-bars" ? (
                        <BarChart data={data} layout="vertical"
                                  margin={{ bottom: modalLayout.bottomMarginPx, left: 0, right: modalLayout.rightMarginPx, top: TOP_MARGIN_PX }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: modalLayout.fontSizePx }} />
                          <YAxis type="category" dataKey="label" width={modalLayout.yAxisWidthPx} interval={0}
                                 tick={{ fontSize: modalLayout.fontSizePx }} />
                          <Tooltip />
                          <Bar dataKey="value" name="Results" shape={barShape}>
                            <LabelList dataKey="value" position="right" style={valueLabelStyle} />
                          </Bar>
                        </BarChart>
                      ) : (
                        <BarChart data={data}
                                  margin={{ bottom: modalLayout.bottomMarginPx, left: modalLayout.leftMarginPx, right: RIGHT_MARGIN_PX, top: TOP_MARGIN_PX }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" angle={-45} interval={0} textAnchor="end" tick={{ fontSize: modalLayout.fontSizePx }} />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="value" name="Results" shape={barShape}>
                            <LabelList dataKey="value" position="top" style={valueLabelStyle} />
                          </Bar>
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          ) : null,
          cancelButtonText: "",
          cancelButtonType: CancelButtonType.TopRight,
          // Modal API requires these even for close-only dialogs
          confirmButtonAction: () => {
          },
          confirmButtonText: "",
          title: "Quinn Bar Chart",
        }}
      />
    </>
  );
};

export default AIChatBarChart;
